import { writable } from 'svelte/store';
import type { ISceneObj } from '../types/SceneType';
import { isDuplicateScene, type DuplicateInfo } from '../components/Scenes/helpers/sceneUtils';

/**
 * Composable hook for duplicate scene detection and warning modal management
 * Provides a consistent interface for components that need duplicate scene protection
 */
export function useDuplicateSceneDetection() {
	const showWarningModal = writable(false);
	const pendingAction = writable<(() => void) | null>(null);
	const duplicateInfo = writable<DuplicateInfo | null>(null);

	/**
	 * Check if a scene is a duplicate and show warning modal if needed
	 * @param sceneId - The scene ID to check
	 * @param allScenes - All scenes store content
	 * @param actionCallback - The action to execute after user confirms
	 * @param actionType - Type of action ('run' or 'enable') for modal customization
	 * @returns true if warning shown, false if action should proceed immediately
	 */
	function checkDuplicateAndWarn(
		sceneId: string | number,
		allScenes: Record<string, ISceneObj>,
		actionCallback: () => void,
		actionType: 'run' | 'enable' = 'run',
	): boolean {
		const duplicateData = isDuplicateScene(sceneId, allScenes);

		if (duplicateData) {
			// Store the pending action and duplicate info
			pendingAction.set(actionCallback);
			duplicateInfo.set(duplicateData);
			showWarningModal.set(true);
			return true; // Warning shown, action postponed
		}

		// Not a duplicate, execute action immediately
		actionCallback();
		return false; // No warning needed
	}

	/**
	 * Handle user confirmation in warning modal
	 */
	function confirmAction() {
		const action = pendingAction.subscribe((fn) => {
			if (fn) {
				fn();
			}
		});
		action();

		// Clean up
		showWarningModal.set(false);
		pendingAction.set(null);
		duplicateInfo.set(null);
	}

	/**
	 * Handle user cancellation in warning modal
	 */
	function cancelAction() {
		showWarningModal.set(false);
		pendingAction.set(null);
		duplicateInfo.set(null);
	}

	return {
		// Stores
		showWarningModal: { subscribe: showWarningModal.subscribe },
		duplicateInfo: { subscribe: duplicateInfo.subscribe },

		// Methods
		checkDuplicateAndWarn,
		confirmAction,
		cancelAction,
	};
}

/**
 * Utility function for simple duplicate checking without modal management
 * Useful for components that need to check duplicates but handle warnings differently
 */
export function checkIfDuplicateScene(
	sceneId: string | number,
	allScenes: Record<string, ISceneObj>,
): DuplicateInfo | null {
	return isDuplicateScene(sceneId, allScenes);
}
